import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Container, Header, Button, Modal, Form, Message, Icon } from 'semantic-ui-react';
import { isICCSupport, isReadOnly } from '../auth';
import { Footer } from '../footer';
import { LoadingWrapper, ReactTable } from '../common';
import { getTeamTypes, createTeamType, updateTeamType } from './teamTypesService';
import {
    EMPTY_TEAM_TYPE_FORM,
    teamTypeToForm,
    validateTeamTypeForm,
    buildTeamTypePayload,
    extractTeamTypeFieldErrors,
    extractTeamTypeFormError,
} from './teamTypeForm';

export const TeamTypes = () => {
    const canEdit = isICCSupport() && !isReadOnly();

    const [teamTypes, setTeamTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [editingType, setEditingType] = useState(null);
    const [formData, setFormData] = useState(EMPTY_TEAM_TYPE_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [modalError, setModalError] = useState('');
    const [saving, setSaving] = useState(false);

    const loadTeamTypes = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const resp = await getTeamTypes();
            if (!resp.ok) throw new Error('Failed to load team types');
            setTeamTypes(await resp.json());
        } catch {
            setError('Failed to load team types. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const hasFetchedRef = useRef(false);
    useEffect(() => {
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        loadTeamTypes();
    }, [loadTeamTypes]);

    const handleFormChange = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }, []);

    const openCreate = () => {
        setEditingType(null);
        setFormData(EMPTY_TEAM_TYPE_FORM);
        setFormErrors({});
        setModalError('');
        setModalOpen(true);
    };

    const openEdit = (teamType) => {
        setEditingType(teamType);
        setFormData(teamTypeToForm(teamType));
        setFormErrors({});
        setModalError('');
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingType(null);
    };

    const handleSave = async () => {
        if (!canEdit || saving) return;
        const errors = validateTeamTypeForm(formData);
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        setSaving(true);
        setModalError('');
        try {
            const payload = buildTeamTypePayload(formData);
            const resp = editingType
                ? await updateTeamType(editingType.id, payload)
                : await createTeamType(payload);

            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setFormErrors(extractTeamTypeFieldErrors(errData));
                setModalError(extractTeamTypeFormError(errData, resp.status) ||
                    (resp.status >= 500 ? 'Failed to save team type. Please try again.' : ''));
                return;
            }

            await loadTeamTypes();
            setSuccess(`Team type "${payload.name}" ${editingType ? 'updated' : 'created'}.`);
            closeModal();
        } catch {
            setModalError('Failed to save team type. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Name',
                accessor: 'name',
                Cell: ({ value }) => <strong>{value}</strong>,
            },
            {
                Header: 'Description',
                accessor: 'description',
                Cell: ({ value }) => value || '—',
            },
        ];

        if (canEdit) {
            cols.push({
                Header: 'Actions',
                id: 'actions',
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button
                            size="small"
                            icon="edit"
                            aria-label={`Edit ${row.original.name}`}
                            title="Edit team type"
                            onClick={() => openEdit(row.original)}
                        />
                    </div>
                ),
            });
        }

        return cols;
    }, [canEdit]);

    return (
        <Container>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.75rem',
                    marginBottom: '1rem',
                    marginTop: '1rem',
                }}
            >
                <Header as="h2" style={{ margin: 0 }}>Team Types</Header>
                {canEdit && (
                    <Button primary icon labelPosition="left" onClick={openCreate}>
                        <Icon name="plus" />
                        Add Team Type
                    </Button>
                )}
            </div>

            {error && (
                <Message negative onDismiss={() => setError('')}>
                    <Message.Content>{error}</Message.Content>
                </Message>
            )}
            {success && (
                <Message positive onDismiss={() => setSuccess('')}>
                    <Message.Content>{success}</Message.Content>
                </Message>
            )}

            <LoadingWrapper loading={loading} text="Loading team types...">
                {teamTypes.length === 0 ? (
                    <Message info content="No team types found." />
                ) : (
                    <ReactTable
                        className="team-types-table react-table-mobile-compact"
                        data={teamTypes}
                        columns={columns}
                        hideGlobalFilter
                    />
                )}
            </LoadingWrapper>

            <Modal open={modalOpen} onClose={closeModal} size="small">
                <Modal.Header>{editingType ? `Edit ${editingType.name}` : 'Add Team Type'}</Modal.Header>
                <Modal.Content>
                    <Form>
                        {modalError && (
                            <Message negative>
                                <Message.Content>{modalError}</Message.Content>
                            </Message>
                        )}
                        <Form.Input
                            label="Name"
                            required
                            maxLength={100}
                            placeholder="Team type name"
                            value={formData.name}
                            error={formErrors.name ? { content: formErrors.name } : false}
                            onChange={(_, { value }) => handleFormChange('name', value)}
                        />
                        <Form.TextArea
                            label="Description"
                            placeholder="Optional description"
                            value={formData.description}
                            error={formErrors.description ? { content: formErrors.description } : false}
                            onChange={(_, { value }) => handleFormChange('description', value)}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={closeModal} disabled={saving}>Cancel</Button>
                    <Button primary loading={saving} disabled={saving} onClick={handleSave}>
                        {editingType ? 'Save' : 'Create'}
                    </Button>
                </Modal.Actions>
            </Modal>

            <Footer />
        </Container>
    );
};
